import history from 'instantsearch.js/es/lib/routers/history';
import { normalizeRefinements } from './algolia-helpers';

const convertTextToSlug = text =>
    text
        .replace(/-/g, '---')
        .toLowerCase()
        .split(' ')
        .map(encodeURIComponent)
        .join('-')
        .replace(/-%26-/g, '-and-');

const convertSlugToText = slug => {
    let text = slug.replace('%C2%AE', '');
    if (text === 'aqualeather' || text === 'batyline') {
        text += '®';
    }

    text = text
        .replace(/-and-/g, '-%26-')
        .split('-')
        .map(decodeURIComponent)
        .join(' ')
        .replace(/ {3}/g, '-');

    return text;
};

const getQueryString = (
    qsModule,
    query = '',
    page = 1,
    collections = [],
    coordinatingFinishes = [],
    categories = [],
    color = [],
    featured = [],
    styles = [],
    durability = [],
    price_tier = [],
    newFilter = null,
    yardFilter = null
) => {
    const queryParameters = {};

    if (newFilter) {
        queryParameters.new = newFilter;
    }

    if (yardFilter) {
        queryParameters.yard = yardFilter;
    }

    if (collections.length > 0) {
        queryParameters.collections = collections.map(encodeURIComponent);
    }
    
    if (coordinatingFinishes.length > 0) {
        queryParameters.coordinatingFinishes = coordinatingFinishes.map(encodeURIComponent);
    }

    if (categories.length > 0) {
        queryParameters.categories = categories.map(encodeURIComponent);
    }

    if (color.length > 0) {
        queryParameters.color = color.map(encodeURIComponent);
    }
    
    if (featured.length > 0) {
        queryParameters.featured = featured.map(encodeURIComponent);
    }

    if (styles.length > 0) {
        queryParameters.styles = styles.map(encodeURIComponent);
    }

    if (durability.length > 0) {
        queryParameters.durability = durability.map(encodeURIComponent);
    }

    if (price_tier.length > 0) {
        queryParameters.price_tier = price_tier.map(encodeURIComponent);
    }

    if (query.length > 0) {
        queryParameters.query = encodeURIComponent(query);
    }

    if (page !== 1) {
        queryParameters.page = page;
    }

    return qsModule.stringify(queryParameters, {
        addQueryPrefix: true,
        arrayFormat: 'repeat',
    });
};

const router = history({

    createURL({ qsModule, routeState, location }) {
        const urlParts = location.href.match(/^(.*?)\/textiles/);
        const baseUrl = `${urlParts ? urlParts[1] : ''}/`;
        let queryString = '';

        // If "New" is selected, make that the URL route
        if (routeState.new) {
            queryString = getQueryString(
                qsModule,
                routeState.query,
                routeState.page,
                routeState.collections,
                routeState.coordinatingFinishes,
                routeState.categories,
                routeState.color,
                routeState.featured,
                routeState.styles,
                routeState.durability,
                routeState.price_tier
            );

            return `${baseUrl}textiles/new/${queryString}`;
        }
        
        // If "Yard" is selected, make that the URL route
        if (routeState.yard) {
            queryString = getQueryString(
                qsModule,
                routeState.query,
                routeState.page,
                routeState.collections,
                routeState.coordinatingFinishes,
                routeState.categories,
                routeState.color,
                routeState.featured,
                routeState.styles,
                routeState.durability,
                routeState.price_tier
            );

            return `${baseUrl}textiles/yard/${queryString}`;
        }

        // Make collection the URL route if other filters don't exist
        if (routeState.collections && routeState.collections.length > 0) {
            const collectionPath = convertTextToSlug(routeState.collections[0]);
            queryString = getQueryString(
                qsModule,
                routeState.query,
                routeState.page,
                (routeState.collections || []).slice(1),
                routeState.coordinatingFinishes,
                routeState.categories,
                routeState.color,
                routeState.featured,
                routeState.styles,
                routeState.durability,
                routeState.price_tier
            );

            return `${baseUrl}textiles/textile/${collectionPath}${queryString}`;
        }
        
        // Make coordinatingFinish the URL route if other filters don't exist
        if (routeState.coordinatingFinishes && routeState.coordinatingFinishes.length > 0) {
            const coordinatingFinishPath = convertTextToSlug(routeState.coordinatingFinishes[0]);
            queryString = getQueryString(
                qsModule,
                routeState.query,
                routeState.page,
                routeState.collections,
                (routeState.coordinatingFinishes || []).slice(1),
                routeState.categories,
                routeState.color,
                routeState.featured,
                routeState.styles,
                routeState.durability,
                routeState.price_tier
            );

            return `${baseUrl}textiles/coordinating-finish/${coordinatingFinishPath}${queryString}`;
        }

        // Make category the URL route if other filters don't exist
        if (routeState.categories && routeState.categories.length > 0) {
            const categoryPath = convertTextToSlug(routeState.categories[0]);
            queryString = getQueryString(
                qsModule,
                routeState.query,
                routeState.page,
                routeState.collections,
                routeState.coordinatingFinishes,
                (routeState.categories || []).slice(1),
                routeState.color,
                routeState.featured,
                routeState.styles,
                routeState.durability,
                routeState.price_tier
            );

            return `${baseUrl}textiles/category/${categoryPath}${queryString}`;
        }

        // Make color the URL route if other filters don't exist
        if (routeState.color && routeState.color.length > 0) {
            const colorPath = convertTextToSlug(routeState.color[0]);
            queryString = getQueryString(
                qsModule,
                routeState.query,
                routeState.page,
                routeState.collections,
                routeState.coordinatingFinishes,
                routeState.categories,
                (routeState.color || []).slice(1),
                routeState.featured,
                routeState.styles,
                routeState.durability,
                routeState.price_tier
            );

            return `${baseUrl}textiles/color/${colorPath}${queryString}`;
        }
        
        // Make featured the URL route if other filters don't exist
        if (routeState.featured && routeState.featured.length > 0) {
            const featuredPath = convertTextToSlug(routeState.featured[0]);
            queryString = getQueryString(
                qsModule,
                routeState.query,
                routeState.page,
                routeState.collections,
                routeState.coordinatingFinishes,
                routeState.categories,
                routeState.color,
                (routeState.featured || []).slice(1),
                routeState.styles,
                routeState.durability,
                routeState.price_tier
            );

            return `${baseUrl}textiles/featured/${featuredPath}${queryString}`;
        }

        // Make style the URL route if other filters don't exist
        if (routeState.styles && routeState.styles.length > 0) {
            const stylePath = convertTextToSlug(routeState.styles[0]);
            queryString = getQueryString(
                qsModule,
                routeState.query,
                routeState.page,
                routeState.collections,
                routeState.coordinatingFinishes,
                routeState.categories,
                routeState.color,
                routeState.featured,
                (routeState.styles || []).slice(1),
                routeState.durability,
                routeState.price_tier
            );

            return `${baseUrl}textiles/style/${stylePath}${queryString}`;
        }

        // Make durability the URL route if other filters don't exist
        if (routeState.durability && routeState.durability.length > 0) {
            const durabilityPath = convertTextToSlug(routeState.durability[0]);
            queryString = getQueryString(
                qsModule,
                routeState.query,
                routeState.page,
                routeState.collections,
                routeState.coordinatingFinishes,
                routeState.categories,
                routeState.color,
                routeState.featured,
                routeState.styles,
                (routeState.durability || []).slice(1),
                routeState.price_tier
            );

            return `${baseUrl}textiles/durability/${durabilityPath}${queryString}`;
        }

        // Make price the URL route if other filters don't exist
        if (routeState.price_tier && routeState.price_tier.length > 0) {
            const pricePath = convertTextToSlug(routeState.price_tier[0]);
            queryString = getQueryString(
                qsModule,
                routeState.query,
                routeState.page,
                routeState.collections,
                routeState.coordinatingFinishes,
                routeState.categories,
                routeState.color,
                routeState.featured,
                routeState.styles,
                routeState.durability,
                (routeState.price_tier || []).slice(1)
            );

            return `${baseUrl}textiles/price-tier/${pricePath}${queryString}`;
        }
        // If no filters exist, don't use URL routing at all
        getQueryString(qsModule, routeState.query, routeState.page);
        return `${baseUrl}textiles/${queryString}`;
    },

    parseURL({ qsModule, location }) {
        const {
            query = '',
            page,
            collections = [],
            coordinatingFinishes = [],
            categories = [],
            color = [],
            featured = [],
            styles = [],
            durability = [],
            price_tier = [],
            new: newFilter,
            yard: yardFilter,
        } = qsModule.parse(location.search.slice(1));

        const newPathnameMatches = location.pathname.match(/new/);
        const yardPathnameMatches = location.pathname.match(/yard/);

        const collectionPathnameMatches = location.pathname.match(/textile\/(.*?)\/?$/);
        const mainCollection = convertSlugToText((collectionPathnameMatches && collectionPathnameMatches[1]) || '');
        const otherCollections = Array.isArray(collections) ? collections : [collections].filter(Boolean);
        
        const coordinatingFinishPathnameMatches = location.pathname.match(/coordinating-finish\/(.*?)\/?$/);
        const mainCoordinatingFinish = convertSlugToText((coordinatingFinishPathnameMatches && coordinatingFinishPathnameMatches[1]) || '');
        const otherCoordinatingFinishes = Array.isArray(coordinatingFinishes) ? coordinatingFinishes : [coordinatingFinishes].filter(Boolean);

        const categoryPathnameMatches = location.pathname.match(/category\/(.*?)\/?$/);
        const mainCategory = convertSlugToText((categoryPathnameMatches && categoryPathnameMatches[1]) || '');
        const otherCategories = Array.isArray(categories) ? categories : [categories].filter(Boolean);

        const colorPathnameMatches = location.pathname.match(/color\/(.*?)\/?$/);
        const mainColor = convertSlugToText((colorPathnameMatches && colorPathnameMatches[1]) || '');
        const otherColors = Array.isArray(color) ? color : [color].filter(Boolean);

        const featuredPathnameMatches = location.pathname.match(/featured\/(.*?)\/?$/);
        const mainFeatured = convertSlugToText((featuredPathnameMatches && featuredPathnameMatches[1]) || '');
        const otherFeatured = Array.isArray(featured) ? featured : [featured].filter(Boolean);

        const stylePathnameMatches = location.pathname.match(/style\/(.*?)\/?$/);
        const mainStyle = convertSlugToText((stylePathnameMatches && stylePathnameMatches[1]) || '');
        const otherStyles = Array.isArray(styles) ? styles : [styles].filter(Boolean);

        const durabilityPathnameMatches = location.pathname.match(/durability\/(.*?)\/?$/);
        const mainDurability = convertSlugToText((durabilityPathnameMatches && durabilityPathnameMatches[1]) || '');
        const otherDurabilitys = Array.isArray(durability) ? durability : [durability].filter(Boolean);

        const pricePathnameMatches = location.pathname.match(/price-tier\/(.*?)\/?$/);
        const mainPrice = convertSlugToText((pricePathnameMatches && pricePathnameMatches[1]) || '');
        const otherPrices = Array.isArray(price_tier) ? price_tier : [price_tier].filter(Boolean);

        return {
            query: decodeURIComponent(query),
            page,
            new: !!newPathnameMatches || newFilter === '1',
            yard: !!yardPathnameMatches || yardFilter === '1',
            mainCollection,
            otherCollections: otherCollections.map(decodeURIComponent),
            mainCoordinatingFinish,
            otherCoordinatingFinishes: otherCoordinatingFinishes.map(decodeURIComponent),
            mainCategory,
            otherCategories: otherCategories.map(decodeURIComponent),
            mainColor,
            otherColors: otherColors.map(decodeURIComponent),
            mainFeatured,
            otherFeatured: otherFeatured.map(decodeURIComponent),
            mainStyle,
            otherStyles: otherStyles.map(decodeURIComponent),
            mainDurability,
            otherDurabilitys: otherDurabilitys.map(decodeURIComponent),
            mainPrice,
            otherPrices: otherPrices.map(decodeURIComponent),
        };
    },
});

const stateMapping = {
    stateToRoute(uiState) {
        uiState.refinementList = normalizeRefinements(uiState.refinementList || []);

        return {
            query: uiState.query,
            page: uiState.page,
            collections: uiState.refinementList && uiState.refinementList.collection,
            coordinatingFinishes: uiState.refinementList && uiState.refinementList.coordinatingFinishes,
            categories: uiState.refinementList && uiState.refinementList.categories,
            color: uiState.refinementList && uiState.refinementList.color,
            featured: uiState.refinementList && uiState.refinementList.featured,
            styles: uiState.refinementList && uiState.refinementList.styles,
            durability: uiState.refinementList && uiState.refinementList.durability,
            price_tier: uiState.refinementList && uiState.refinementList.price_tier,
            new: uiState.toggle && uiState.toggle.new && 1,
            yard: uiState.toggle && uiState.toggle.yard && 1,
        };
    },

    routeToState(routeState) {
        const state = {
            query: routeState.query,
            page: routeState.page,
            menu: {},
            toggle: {},
            refinementList: {
                collection: [],
                coordinatingFinishes: [],
                categories: [],
                color: [],
                featured: [],
                styles: [],
                durability: [],
                price_tier: [],
            },
        };

        if (routeState.new) {
            state.toggle.new = true;
        }
        
        if (routeState.yard) {
            state.toggle.yard = true;
        }

        if (routeState.mainCollection) {
            state.refinementList.collection.push(routeState.mainCollection);
        }

        if (routeState.otherCollections.length > 0) {
            state.refinementList.collection = state.refinementList.collection.concat(routeState.otherCollections);
        }
        
        if (routeState.mainCoordinatingFinish) {
            state.refinementList.coordinatingFinishes.push(routeState.mainCoordinatingFinish);
        }

        if (routeState.otherCoordinatingFinishes.length > 0) {
            state.refinementList.coordinatingFinishes = state.refinementList.coordinatingFinishes.concat(routeState.otherCoordinatingFinishes);
        }

        if (routeState.mainCategory) {
            state.refinementList.categories.push(routeState.mainCategory);
        }

        if (routeState.otherCategories.length > 0) {
            state.refinementList.categories = state.refinementList.categories.concat(routeState.otherCategories);
        }

        if (routeState.mainColor) {
            state.refinementList.color.push(routeState.mainColor);
        }

        if (routeState.otherColors.length > 0) {
            state.refinementList.color = state.refinementList.color.concat(routeState.otherColors);
        }
        
        if (routeState.mainFeatured) {
            state.refinementList.featured.push(routeState.mainFeatured);
        }

        if (routeState.otherFeatured.length > 0) {
            state.refinementList.featured = state.refinementList.featured.concat(routeState.otherFeatured);
        }

        if (routeState.mainStyle) {
            state.refinementList.styles.push(routeState.mainStyle);
        }

        if (routeState.otherStyles.length > 0) {
            state.refinementList.styles = state.refinementList.styles.concat(routeState.otherStyles);
        }

        if (routeState.mainDurability) {
            state.refinementList.durability.push(routeState.mainDurability);
        }

        if (routeState.otherDurabilitys.length > 0) {
            state.refinementList.durability = state.refinementList.durability.concat(routeState.otherDurabilitys);
        }

        if (routeState.mainPrice) {
            state.refinementList.price_tier.push(routeState.mainPrice);
        }

        if (routeState.otherPrices.length > 0) {
            state.refinementList.price_tier = state.refinementList.price_tier.concat(routeState.otherPrices);
        }

        return state;
    },
};

export const textilesRouting = {
    router,
    stateMapping,
};
